import React from 'react';
import { cva } from 'class-variance-authority';
import type {
  ButtonVariant,
  DisabledButtonVariant,
} from './button-common.types';
import type { IconLike } from '../icon/icon.types';

export const iconSizeByButtonSize: Record<
  'small' | 'medium' | 'large',
  number
> = {
  small: 16,
  medium: 16,
  large: 24,
};

export const BUTTON_VARIANT = {
  DEFAULT_FILLED: 'default-filled',
  DEFAULT_OUTLINE: 'default-outline',
  DEFAULT_GHOST: 'default-ghost',
  DESTRUCTIVE_FILLED: 'destructive-filled',
  DESTRUCTIVE_OUTLINE: 'destructive-outline',
  DESTRUCTIVE_GHOST: 'destructive-ghost',
  DARK_FILLED: 'dark-filled',
  DARK_OUTLINE: 'dark-outline',
  DARK_GHOST: 'dark-ghost',
  LIGHT_FILLED: 'light-filled',
  LIGHT_OUTLINE: 'light-outline',
  LIGHT_GHOST: 'light-ghost',
} as const;

export const DISABLED_BUTTON_VARIANT = {
  DISABLED_DEFAULT_FILLED: 'disabled-default-filled',
  DISABLED_DEFAULT_OUTLINE: 'disabled-default-outline',
  DISABLED_DEFAULT_GHOST: 'disabled-default-ghost',
  DISABLED_DESTRUCTIVE_FILLED: 'disabled-destructive-filled',
  DISABLED_DESTRUCTIVE_OUTLINE: 'disabled-destructive-outline',
  DISABLED_DESTRUCTIVE_GHOST: 'disabled-destructive-ghost',
  DISABLED_DARK_FILLED: 'disabled-dark-filled',
  DISABLED_DARK_OUTLINE: 'disabled-dark-outline',
  DISABLED_DARK_GHOST: 'disabled-dark-ghost',
  DISABLED_LIGHT_FILLED: 'disabled-light-filled',
  DISABLED_LIGHT_OUTLINE: 'disabled-light-outline',
  DISABLED_LIGHT_GHOST: 'disabled-light-ghost',
} as const;

const affixStyles: Record<ButtonVariant, string> = {
  [BUTTON_VARIANT.DEFAULT_FILLED]: '[&_.affix]:text-fill-white-static',
  [BUTTON_VARIANT.DEFAULT_OUTLINE]:
    '[&_.affix]:text-fill-brand-primary hover:[&_.affix]:text-text-brand-secondary active:[&_.affix]:text-text-brand-tertiary',
  [BUTTON_VARIANT.DEFAULT_GHOST]:
    '[&_.affix]:text-fill-brand-primary hover:[&_.affix]:text-text-brand-secondary active:[&_.affix]:text-text-brand-tertiary',

  [BUTTON_VARIANT.DESTRUCTIVE_FILLED]: '[&_.affix]:text-fill-white-static',
  [BUTTON_VARIANT.DESTRUCTIVE_OUTLINE]:
    '[&_.affix]:text-fill-destructive-primary hover:[&_.affix]:text-text-destructive-secondary active:[&_.affix]:text-text-destructive-tertiary',
  [BUTTON_VARIANT.DESTRUCTIVE_GHOST]:
    '[&_.affix]:text-fill-destructive-primary hover:[&_.affix]:text-text-destructive-secondary active:[&_.affix]:text-text-destructive-tertiary',

  [BUTTON_VARIANT.DARK_FILLED]: '[&_.affix]:text-fill-white-default',
  [BUTTON_VARIANT.DARK_OUTLINE]:
    '[&_.affix]:text-fill-neutral-primary hover:[&_.affix]:text-text-neutral-secondary active:[&_.affix]:text-text-neutral-tertiary',
  [BUTTON_VARIANT.DARK_GHOST]:
    '[&_.affix]:text-fill-neutral-primary hover:[&_.affix]:text-text-neutral-secondary active:[&_.affix]:text-text-neutral-tertiary',

  [BUTTON_VARIANT.LIGHT_FILLED]:
    '[&_.affix]:text-fill-neutral-static dark:[&_.affix]:text-fill-white-static',
  [BUTTON_VARIANT.LIGHT_OUTLINE]:
    '[&_.affix]:text-fill-white-static hover:[&_.affix]:text-text-white-secondary active:[&_.affix]:text-text-white-tertiary dark:[&_.affix]:text-fill-white-default dark:hover:[&_.affix]:text-text-white-tertiary dark:active:[&_.affix]:text-text-white-secondary',
  [BUTTON_VARIANT.LIGHT_GHOST]:
    '[&_.affix]:text-fill-white-static hover:[&_.affix]:text-text-white-secondary active:[&_.affix]:text-text-white-tertiary dark:[&_.affix]:text-fill-white-default dark:hover:[&_.affix]:text-text-white-tertiary dark:active:[&_.affix]:text-text-white-secondary',
};

const focusColor: Record<ButtonVariant, string> = {
  [BUTTON_VARIANT.DEFAULT_FILLED]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
  [BUTTON_VARIANT.DEFAULT_OUTLINE]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
  [BUTTON_VARIANT.DEFAULT_GHOST]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',

  [BUTTON_VARIANT.DESTRUCTIVE_FILLED]:
    'focus-visible:outline-[var(--color-bg-destructive-primary)]',
  [BUTTON_VARIANT.DESTRUCTIVE_OUTLINE]:
    'focus-visible:outline-[var(--color-bg-destructive-primary)]',
  [BUTTON_VARIANT.DESTRUCTIVE_GHOST]:
    'focus-visible:outline-[var(--color-bg-destructive-primary)]',

  [BUTTON_VARIANT.DARK_FILLED]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
  [BUTTON_VARIANT.DARK_OUTLINE]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
  [BUTTON_VARIANT.DARK_GHOST]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',

  [BUTTON_VARIANT.LIGHT_FILLED]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
  [BUTTON_VARIANT.LIGHT_OUTLINE]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
  [BUTTON_VARIANT.LIGHT_GHOST]:
    'focus-visible:outline-[var(--color-bg-brand-primary)]',
};

const hoverStyles: Record<ButtonVariant, string> = {
  [BUTTON_VARIANT.DEFAULT_FILLED]: 'hover:bg-bg-brand-secondary',
  [BUTTON_VARIANT.DEFAULT_OUTLINE]:
    'hover:bg-bg-brand-subtle hover:shadow-[0_0_0_1px_var(--color-border-brand-primary)] hover:text-text-brand-secondary',
  [BUTTON_VARIANT.DEFAULT_GHOST]: 'hover:text-text-brand-secondary',

  [BUTTON_VARIANT.DESTRUCTIVE_FILLED]: 'hover:bg-bg-destructive-secondary',
  [BUTTON_VARIANT.DESTRUCTIVE_OUTLINE]:
    'hover:bg-bg-destructive-subtle hover:shadow-[0_0_0_1px_var(--color-border-destructive-primary)] hover:text-text-destructive-secondary',
  [BUTTON_VARIANT.DESTRUCTIVE_GHOST]: 'hover:text-text-destructive-secondary',

  [BUTTON_VARIANT.DARK_FILLED]: 'hover:bg-bg-neutral-secondary',
  [BUTTON_VARIANT.DARK_OUTLINE]:
    'hover:bg-bg-neutral-subtle hover:shadow-[0_0_0_1px_var(--color-border-neutral-secondary)] hover:text-text-neutral-secondary',
  [BUTTON_VARIANT.DARK_GHOST]: 'hover:text-text-neutral-secondary',

  [BUTTON_VARIANT.LIGHT_FILLED]:
    'hover:bg-bg-inverse-secondary dark:hover:bg-bg-subtle',
  [BUTTON_VARIANT.LIGHT_OUTLINE]:
    'hover:bg-bg-inverse-subtle hover:shadow-[0_0_0_1px_var(--color-border-inverse-secondary)] hover:text-text-white-secondary dark:hover:bg-[var(--neutral-50)] dark:hover:text-text-white-tertiary',
  [BUTTON_VARIANT.LIGHT_GHOST]:
    'hover:text-text-white-secondary dark:hover:text-text-white-tertiary',
};

const activeStyles: Record<ButtonVariant, string> = {
  [BUTTON_VARIANT.DEFAULT_FILLED]: 'active:bg-bg-brand-tertiary',
  [BUTTON_VARIANT.DEFAULT_OUTLINE]:
    'active:bg-bg-brand-soft active:shadow-[0_0_0_1px_var(--color-border-brand-primary)] active:text-text-brand-tertiary',
  [BUTTON_VARIANT.DEFAULT_GHOST]: 'active:text-text-brand-tertiary',

  [BUTTON_VARIANT.DESTRUCTIVE_FILLED]: 'active:bg-bg-destructive-tertiary',
  [BUTTON_VARIANT.DESTRUCTIVE_OUTLINE]:
    'active:bg-bg-destructive-soft active:shadow-[0_0_0_1px_var(--color-border-destructive-primary)] active:text-text-destructive-tertiary',
  [BUTTON_VARIANT.DESTRUCTIVE_GHOST]: 'active:text-text-destructive-tertiary',

  [BUTTON_VARIANT.DARK_FILLED]: 'active:bg-bg-neutral-tertiary',
  [BUTTON_VARIANT.DARK_OUTLINE]:
    'active:bg-bg-neutral-soft active:shadow-[0_0_0_1px_var(--color-border-neutral-secondary)] active:text-text-neutral-tertiary',
  [BUTTON_VARIANT.DARK_GHOST]: 'active:text-text-neutral-tertiary',

  [BUTTON_VARIANT.LIGHT_FILLED]:
    'active:bg-bg-inverse-tertiary dark:active:bg-bg-soft',
  [BUTTON_VARIANT.LIGHT_OUTLINE]:
    'active:bg-bg-inverse-soft active:shadow-[0_0_0_1px_var(--color-border-inverse-tertiary)] active:text-text-white-tertiary dark:active:bg-[var(--neutral-100)] dark:active:shadow-[0_0_0_1px_var(--color-border-neutral-secondary)] dark:active:text-text-white-secondary',
  [BUTTON_VARIANT.LIGHT_GHOST]:
    'active:text-text-white-tertiary dark:active:text-text-white-secondary',
};

const baseStyles: Record<ButtonVariant, string> = {
  [BUTTON_VARIANT.DEFAULT_FILLED]:
    'bg-bg-brand-primary text-fg-on-strong dark:text-text-white-primary',
  [BUTTON_VARIANT.DEFAULT_OUTLINE]:
    'bg-transparent text-text-brand-primary shadow-[0_0_0_1px_var(--color-border-brand-secondary)]',
  [BUTTON_VARIANT.DEFAULT_GHOST]: 'bg-transparent text-text-brand-primary',

  [BUTTON_VARIANT.DESTRUCTIVE_FILLED]:
    'bg-bg-destructive-primary text-fg-on-strong',
  [BUTTON_VARIANT.DESTRUCTIVE_OUTLINE]:
    'bg-transparent text-text-destructive-primary shadow-[0_0_0_1px_var(--color-border-destructive-secondary)]',
  [BUTTON_VARIANT.DESTRUCTIVE_GHOST]:
    'bg-transparent text-text-destructive-primary',

  [BUTTON_VARIANT.DARK_FILLED]: 'bg-bg-neutral-primary text-fg-default',
  [BUTTON_VARIANT.DARK_OUTLINE]:
    'bg-transparent text-text-neutral-primary shadow-[0_0_0_1px_var(--color-border-neutral-primary)]',
  [BUTTON_VARIANT.DARK_GHOST]: 'bg-transparent text-text-neutral-primary',

  [BUTTON_VARIANT.LIGHT_FILLED]:
    'bg-bg-inverse-primary text-on-static-light dark:bg-bg-inverse-subtle dark:text-fg-on-strong',
  [BUTTON_VARIANT.LIGHT_OUTLINE]:
    'bg-transparent text-text-white-primary shadow-[0_0_0_1px_var(--color-border-inverse-primary)] dark:text-fg-default dark:shadow-[0_0_0_1px_var(--color-border-neutral-primary)]',
  [BUTTON_VARIANT.LIGHT_GHOST]:
    'bg-transparent text-text-white-primary dark:text-fg-default',
};

const disabledStyles: Record<DisabledButtonVariant, string> = {
  [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_FILLED]:
    'bg-bg-disabled-default text-text-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_OUTLINE]:
    'bg-bg-disabled-soft shadow-[0_0_0_1px_var(--color-border-muted)] text-text-disabled-soft',
  [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_GHOST]:
    'bg-transparent text-text-disabled-soft',

  [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_FILLED]:
    'bg-bg-disabled-default text-text-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_OUTLINE]:
    'bg-bg-disabled-soft shadow-[0_0_0_1px_var(--color-border-muted)] text-text-disabled-soft',
  [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_GHOST]:
    'bg-transparent text-text-disabled-soft',

  [DISABLED_BUTTON_VARIANT.DISABLED_DARK_FILLED]:
    'bg-bg-disabled-default text-text-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_DARK_OUTLINE]:
    'bg-bg-disabled-soft shadow-[0_0_0_1px_var(--color-border-muted)] text-text-disabled-soft',
  [DISABLED_BUTTON_VARIANT.DISABLED_DARK_GHOST]:
    'bg-transparent text-text-disabled-soft',

  [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_FILLED]:
    'bg-bg-disabled-inverse text-text-disabled-inverse dark:bg-[var(--neutral-200)] dark:text-text-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_OUTLINE]:
    'bg-bg-disabled-inverse-soft shadow-[0_0_0_1px_var(--neutral-700)] text-text-disabled-inverse-soft dark:bg-[var(--neutral-50)] dark:shadow-[0_0_0_1px_var(--neutral-300)] dark:text-text-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_GHOST]:
    'bg-transparent text-text-disabled-inverse-soft dark:text-text-disabled-default',
};

const disabledAffixStyles: Record<DisabledButtonVariant, string> = {
  [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_FILLED]:
    '[&_.affix]:text-fill-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_OUTLINE]:
    '[&_.affix]:text-fill-disabled-soft',
  [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_GHOST]:
    '[&_.affix]:text-fill-disabled-soft',

  [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_FILLED]:
    '[&_.affix]:text-fill-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_OUTLINE]:
    '[&_.affix]:text-fill-disabled-soft',
  [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_GHOST]:
    '[&_.affix]:text-fill-disabled-soft',

  [DISABLED_BUTTON_VARIANT.DISABLED_DARK_FILLED]:
    '[&_.affix]:text-fill-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_DARK_OUTLINE]:
    '[&_.affix]:text-fill-disabled-soft',
  [DISABLED_BUTTON_VARIANT.DISABLED_DARK_GHOST]:
    '[&_.affix]:text-fill-disabled-soft',

  [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_FILLED]:
    '[&_.affix]:text-fill-disabled-inverse dark:[&_.affix]:text-fill-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_OUTLINE]:
    '[&_.affix]:text-fill-disabled-inverse-soft dark:[&_.affix]:text-fill-disabled-default',
  [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_GHOST]:
    '[&_.affix]:text-fill-disabled-inverse-soft dark:[&_.affix]:text-fill-disabled-default',
};

export const getStyles = (variant: ButtonVariant) => {
  return [
    baseStyles[variant],
    affixStyles[variant],
    hoverStyles[variant],
    activeStyles[variant],
    focusColor[variant],
  ]
    .filter(Boolean)
    .join(' ');
};

export const getDisabledStyles = (variant: DisabledButtonVariant) => {
  return [disabledStyles[variant], disabledAffixStyles[variant], 'cursor-auto']
    .filter(Boolean)
    .join(' ');
};

export const buttonVariants = cva(
  'inline-flex font-display justify-center items-center w-fit box-border border-none cursor-pointer focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 no-underline visited:no-underline visited:cursor-pointer',
  {
    variants: {
      variant: {
        [BUTTON_VARIANT.DEFAULT_FILLED]: getStyles(
          BUTTON_VARIANT.DEFAULT_FILLED
        ),
        [BUTTON_VARIANT.DEFAULT_OUTLINE]: getStyles(
          BUTTON_VARIANT.DEFAULT_OUTLINE
        ),
        [BUTTON_VARIANT.DEFAULT_GHOST]: getStyles(BUTTON_VARIANT.DEFAULT_GHOST),

        [BUTTON_VARIANT.DESTRUCTIVE_FILLED]: getStyles(
          BUTTON_VARIANT.DESTRUCTIVE_FILLED
        ),
        [BUTTON_VARIANT.DESTRUCTIVE_OUTLINE]: getStyles(
          BUTTON_VARIANT.DESTRUCTIVE_OUTLINE
        ),
        [BUTTON_VARIANT.DESTRUCTIVE_GHOST]: getStyles(
          BUTTON_VARIANT.DESTRUCTIVE_GHOST
        ),

        [BUTTON_VARIANT.DARK_FILLED]: getStyles(BUTTON_VARIANT.DARK_FILLED),
        [BUTTON_VARIANT.DARK_OUTLINE]: getStyles(BUTTON_VARIANT.DARK_OUTLINE),
        [BUTTON_VARIANT.DARK_GHOST]: getStyles(BUTTON_VARIANT.DARK_GHOST),

        [BUTTON_VARIANT.LIGHT_FILLED]: getStyles(BUTTON_VARIANT.LIGHT_FILLED),
        [BUTTON_VARIANT.LIGHT_OUTLINE]: getStyles(BUTTON_VARIANT.LIGHT_OUTLINE),
        [BUTTON_VARIANT.LIGHT_GHOST]: getStyles(BUTTON_VARIANT.LIGHT_GHOST),

        [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_FILLED]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_FILLED
        ),
        [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_OUTLINE]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_OUTLINE
        ),
        [DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_GHOST]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DEFAULT_GHOST
        ),

        [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_FILLED]:
          getDisabledStyles(
            DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_FILLED
          ),
        [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_OUTLINE]:
          getDisabledStyles(
            DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_OUTLINE
          ),
        [DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_GHOST]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DESTRUCTIVE_GHOST
        ),

        [DISABLED_BUTTON_VARIANT.DISABLED_DARK_FILLED]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DARK_FILLED
        ),
        [DISABLED_BUTTON_VARIANT.DISABLED_DARK_OUTLINE]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DARK_OUTLINE
        ),
        [DISABLED_BUTTON_VARIANT.DISABLED_DARK_GHOST]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_DARK_GHOST
        ),

        [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_FILLED]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_FILLED
        ),
        [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_OUTLINE]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_OUTLINE
        ),
        [DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_GHOST]: getDisabledStyles(
          DISABLED_BUTTON_VARIANT.DISABLED_LIGHT_GHOST
        ),
      },
      round: {
        false: 'rounded-sm',
        true: 'rounded-full',
      },
      size: {
        small:
          'min-h-[32px] px-(--spacing-xl) py-(--spacing-sm) leading-md text-(length:--text-md) gap-(--spacing-xs)',
        medium:
          'min-h-[40px] px-(--spacing-2xl) py-(--spacing-md) leading-lg text-(length:--text-lg) gap-(--spacing-xs)',
        large:
          'min-h-[48px] px-(--spacing-2xl) py-(--spacing-md) leading-2xl text-(length:--text-2xl) gap-(--spacing-sm)',
      },
    },
  }
);

export const renderAffix = (
  affix: IconLike | React.ReactNode,
  size: 'small' | 'medium' | 'large'
) => {
  if (typeof affix === 'function') {
    const AffixComponent = affix as IconLike;
    return (
      <AffixComponent size={iconSizeByButtonSize[size]} className="affix" />
    );
  }
  return <div className="affix flex items-center">{affix}</div>;
};
